import { useState } from "react";
import { Link } from "react-router-dom";

import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/Popover";
import { useNotifications } from "../data/api/notifications";
import { useRealtimeScope } from "../data/realtime/useRealtimeScope";
import type { NotificationKind } from "../data/types";
import { Icon } from "../icons";
import styles from "./NotificationsBell.module.css";
import { cx } from "../lib/cx";
import { relTime } from "../lib/time";
import { paths } from "../routing/paths";

const kindIcon = (kind: NotificationKind) => {
  switch (kind) {
    case "done":
      return <Icon.Check size={14} />;
    case "needs-input":
      return <Icon.Help size={14} />;
    case "review":
      return <Icon.PullRequest size={14} />;
    default:
      return <Icon.Bell size={14} />;
  }
};

const kindClass = (kind: NotificationKind): string =>
  cx(
    styles.ico,
    kind === "needs-input" && styles.needsInput,
    kind === "review" && styles.review,
    kind === "done" && styles.done,
  );

export const NotificationsBell = () => {
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const { markAllRead, markRead, notifications, refetch, unreadCount } =
    useNotifications();
  // Live badge: the server publishes notification.posted / notification.read on
  // the user's own scope, so refetch the feed whenever it changes.
  useRealtimeScope(user ? `user:${user.public_id}` : null, refetch);

  const onOpenChange = (next: boolean): void => {
    setOpen(next);
    if (next) {
      refetch();
    }
  };

  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={styles.trigger}
          icon
          title="Notifications"
          variant="ghost"
        >
          <Icon.Bell size={15} />
          {unreadCount > 0 && (
            <span className={styles.badge}>{unreadCount}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={styles.menu} sideOffset={6}>
        <div className={styles.head}>
          <span className={styles.title}>Notifications</span>
          <span className={styles.spacer} />
          {unreadCount > 0 && (
            <Button onClick={markAllRead} size="sm" variant="ghost">
              Mark all read
            </Button>
          )}
        </div>
        <div className={styles.list}>
          {notifications.length === 0 && (
            <div className={styles.empty}>You&apos;re all caught up.</div>
          )}
          {notifications.map((n) => {
            const body = (
              <>
                <span className={kindClass(n.kind)}>{kindIcon(n.kind)}</span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div className={styles.text}>{n.text}</div>
                  <div className={styles.meta}>
                    {n.taskLabel ? `${n.taskLabel} · ` : ""}
                    {relTime(n.t)}
                  </div>
                </div>
                {!n.read && <span className={styles.unreadDot} />}
              </>
            );
            const itemClass = cx(styles.item, !n.read && styles.unread);
            const dismiss = (): void => {
              markRead(n.id);
              setOpen(false);
            };
            // A notification that points at a task is a link to it; one without
            // a target only marks itself read, so it stays a button.
            return n.repoId && n.taskLabel ? (
              <Link
                className={itemClass}
                key={n.id}
                onClick={dismiss}
                to={paths.task(n.repoId, n.taskLabel)}
              >
                {body}
              </Link>
            ) : (
              <button
                className={itemClass}
                key={n.id}
                onClick={dismiss}
                type="button"
              >
                {body}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
